/**
 * "Your numbers are gross" onboarding-checklist nag. The dashboard can show
 * revenue-only (gross) numbers before setup is fully done — costs aren't required
 * to unlock — so this makes that loud and honest: the FULL onboarding checklist
 * (account → store → business costs → contribution margin → organize catalog) in
 * order, with what's done vs not, shown big and red on the dashboard until every
 * step is complete.
 *
 * Big + annoying by design: it returns every day until done. The founder can X
 * it ("remind me tomorrow") — we remember the dismissal date and bring it back on
 * the next calendar day. Once the whole checklist is complete it never shows.
 *
 * TWO tones (founder 2026-07-12). RED when the INITIAL setup was never finished —
 * the loud "your numbers are gross" checklist. AMBER when the account is already
 * set up (dashboard unlocked) and only optional / new-arrival work is left — a
 * concise, informational "1 new product started selling and needs a quick review,
 * everything else is still done", so the box says WHY it came back instead of
 * implying nothing works. The gate hands us `setupReason` to pick the tone + copy.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useOnboardingChecklist, type SetupReason } from "./onboarding-checklist.js";
import { accountScopedStorageKey } from "./account-browser-state.js";
import { useRequiredActiveAccountId } from "./account-context.js";

export function costNagDismissStorageKey(accountId: string): string {
  return accountScopedStorageKey(accountId, "cost-nag-dismissed-date");
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The amber "here's what's new" copy for an already-set-up account (dashboard
 * unlocked, only optional / new-arrival work left). Pure + exported so the exact
 * plain-language string is easy to eyeball and lock. The RED "your numbers are
 * gross" nag (initial setup never finished) is rendered inline below, unchanged.
 */
export function amberContent(reason: SetupReason): { title: string; body: string; cta: string } {
  const n = reason.reviewCount;
  if (n > 0) {
    const noun = n === 1 ? "product" : "products";
    const verb = n === 1 ? "needs" : "need";
    const poss = n === 1 ? "its" : "their";
    // All the pending reviews are brand-new arrivals → say so ("a new product
    // started selling"), which is the whole point: the box came back for a reason.
    if (reason.newlySellingCount >= n) {
      return {
        title: n === 1 ? "1 new product to review" : `${n} new products to review`,
        body: `${n === 1 ? "A new product" : `${n} new products`} started selling and ${verb} a quick review before ${poss} true profit shows. Everything else is still done.`,
        cta: n === 1 ? "Review it now" : "Review them now",
      };
    }
    return {
      title: `${n} ${noun} to review`,
      body: `${n} ${noun} ${verb} a quick review before ${poss} true profit shows. Everything else is still done.`,
      cta: n === 1 ? "Review it now" : "Review them now",
    };
  }
  // Incremental, but nothing in the review queue — a product that has sold still
  // needs its costs entered before that product's true profit shows.
  return {
    title: "A little cost setup left",
    body: "Some products still need their costs entered before their true profit shows. Everything else is done.",
    cta: "Add costs",
  };
}

export function CostReadinessBanner(): React.ReactElement | null {
  const location = useLocation();
  const accountId = useRequiredActiveAccountId();
  const dismissKey = costNagDismissStorageKey(accountId);
  const today = todayStr();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(dismissKey) === today; } catch { return false; }
  });
  const { items, complete, loading, state } = useOnboardingChecklist();

  // Never nag on the setup pages themselves (they're already doing it there),
  // and never on the agency/operator pages — the checklist belongs to the
  // CLIENT account being viewed; the operator reviewing their portfolio
  // shouldn't be nagged to finish a client's setup (founder 2026-07-05).
  const onSetup = location.pathname.startsWith("/welcome") || location.pathname.startsWith("/onboarding");
  const onAgency = location.pathname.startsWith("/agency") || location.pathname.startsWith("/admin");
  if (onSetup || onAgency || loading || items.length === 0 || dismissed) return null;

  const dismiss = (): void => {
    try { localStorage.setItem(dismissKey, today); } catch { /* private mode — just hide for now */ }
    setDismissed(true);
  };
  const next = items.find((i) => !i.done);
  const remaining = items.filter((i) => !i.done).length;

  // AMBER — the account already finished onboarding (this banner only ever renders
  // once the dashboard is UNLOCKED), so it's not "everything is gross" — the numbers
  // are live. The one thing that still belongs in a top banner is genuine NEW work:
  // a product that started selling and needs a quick review before ITS profit shows.
  // Missing costs on already-known products surface per-card via CmGapNotice (the
  // founder's 2026-07-11 disclosure model), not here — so when there's nothing new
  // to review, this box is GONE (founder 2026-07-12: "as soon as they finish that
  // it goes away"). Confirming the flagged SKU invalidates the gate → reviewCount
  // drops to 0 → this returns null on the next render, no reload.
  const reason = state?.setupReason ?? null;
  if (reason?.incremental) {
    if (reason.reviewCount === 0) return null;
    const amber = amberContent(reason);
    return (
      <div style={styles.wrapAmber} role="status">
        <button aria-label="Dismiss until tomorrow" onClick={dismiss} style={styles.close}>✕</button>
        <div style={styles.titleAmber}>{amber.title}</div>
        <div style={styles.body}>{amber.body}</div>
        <div style={styles.actions}>
          <Link to={next?.href ?? "/welcome"} style={styles.cta}>{amber.cta} →</Link>
          <button onClick={dismiss} style={styles.later}>Remind me tomorrow</button>
        </div>
      </div>
    );
  }

  // RED — initial setup not finished (or an older API that didn't send a reason),
  // so the numbers really are gross: keep the loud, full strict checklist.
  if (complete) return null;
  return (
    <div style={styles.wrap} role="alert">
      <button aria-label="Dismiss until tomorrow" onClick={dismiss} style={styles.close}>✕</button>
      <div style={styles.title}>⚠ Your numbers are GROSS — not your true profit</div>
      <div style={styles.body}>
        Margin, CM-ROAS, LTV and profit below are based on <strong>revenue only</strong> until setup is finished.
        Complete every step and your numbers become real:
      </div>
      <ul style={styles.list}>
        {items.map((i) => (
          <li key={i.step} style={{ ...styles.item, color: i.done ? "var(--good)" : "var(--text)" }}>
            <span style={styles.checkbox} aria-hidden="true">{i.done ? "✓" : "○"}</span>
            <span style={i.done ? { textDecoration: "line-through", opacity: 0.7 } : undefined}>{i.name}</span>
          </li>
        ))}
      </ul>
      <div style={styles.actions}>
        <Link to={next?.href ?? "/welcome"} style={styles.cta}>Finish setup{remaining > 0 ? ` — ${remaining} left` : ""} →</Link>
        <button onClick={dismiss} style={styles.later}>Remind me tomorrow</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    position: "relative",
    background: "color-mix(in srgb, var(--bad) 12%, var(--surface))",
    border: "1.5px solid color-mix(in srgb, var(--bad) 55%, var(--border))",
    borderRadius: "var(--r-sm)",
    padding: "16px 18px",
    marginBottom: 24,
  },
  // Amber variant — post-setup "here's what's new", informational not alarming.
  wrapAmber: {
    position: "relative",
    background: "color-mix(in srgb, var(--c2) 10%, var(--surface))",
    border: "1.5px solid color-mix(in srgb, var(--c2) 45%, var(--border))",
    borderRadius: "var(--r-sm)",
    padding: "16px 18px",
    marginBottom: 24,
  },
  titleAmber: { fontSize: 15, fontWeight: 800, color: "var(--c2)", marginBottom: 6, letterSpacing: "-0.01em" },
  close: {
    position: "absolute", top: 10, right: 12,
    background: "transparent", border: "none", cursor: "pointer",
    color: "var(--text-faint)", fontSize: 14, lineHeight: 1, padding: 4,
  },
  title: { fontSize: 15, fontWeight: 800, color: "var(--bad)", marginBottom: 6, letterSpacing: "-0.01em" },
  body: { fontSize: 13, color: "var(--text)", lineHeight: 1.55, maxWidth: 720, marginBottom: 10 },
  list: { listStyle: "none", margin: "0 0 12px", padding: 0, display: "grid", gap: 5, gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" },
  item: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500 },
  checkbox: { width: 16, textAlign: "center", flexShrink: 0, fontWeight: 700 },
  actions: { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" },
  cta: {
    background: "var(--accent)", color: "var(--accent-contrast)",
    padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700,
    textDecoration: "none",
  },
  later: {
    background: "transparent", border: "none", cursor: "pointer",
    color: "var(--text-dim)", fontSize: 12.5, fontWeight: 600, textDecoration: "underline",
  },
};
